import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useAppStore } from '../store/appStore';
import { getDefaultPermissionsForRole } from '../utils/moduleAccess';
import './Auth.css';

export default function Login() {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { setToken, setUser, setTimezone, setPermissions } = useAuthStore();
  const resetAppStore = useAppStore((state) => state.resetAppStore);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Clear all previous user data
      resetAppStore();
      
      const response = await authAPI.login(formData.username, formData.password);
      const { access_token, refresh_token, expires_in, timezone } = response.data;
      
      // Set token and refresh token (expires_in in seconds)
      setToken(access_token, refresh_token, expires_in);
      if (timezone) {
        setTimezone(timezone);
      }
      
      // Fetch full user data including depot_id
      const userInfoResponse = await authAPI.getCurrentUser();
      const userData = userInfoResponse.data;
      
      // Check if user is verificador - they cannot access the website
      if (userData.user_type === 'verificador') {
        toast.error('Verificador users can only access the mobile app. Please use your mobile device to login.');
        setToken(null); // Clear the token since we rejected this user
        setLoading(false);
        return;
      }
      
      // Store full user object with all fields including depot_id
      setUser({
        id: userData.id,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        email: userData.email,
        user_type: userData.user_type,
        company_id: userData.company_id,
        depot_id: userData.depot_id,
        is_active: userData.is_active,
        timezone: userData.timezone,
      });
      setPermissions(getDefaultPermissionsForRole(userData.user_type));
      
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-form">
        <h1>Seals App</h1>
        <img
          src="/logo-login.png"
          alt="Seals App"
          className="auth-logo"
        />
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <p className="auth-link">
          Forgot your password? <Link to="/reset-password">Reset it here</Link>
        </p>
      </div>
    </div>
  );
}
